/**
 * Arroba System - Native Predictive Search Component
 * Integrates directly with Shopify's Predictive Search API & Search & Discovery App
 */
(function() {
  'use strict';

  class PredictiveSearchHandler {
    constructor() {
      this.debounceTime = 250;
      this.minChars = 2;
      this.abortController = null;
      this.activeDropdown = null;
      this.focusedIndex = -1;
      this.cachedResults = new Map();

      this.init();
    }

    init() {
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', () => this.bindEvents());
      } else {
        this.bindEvents();
      }
    }

    bindEvents() {
      // Find all search inputs in the store
      const searchInputs = document.querySelectorAll(
        '#header-search-form input[name="q"], #blog-search-form input[name="q"], .search-bar input[name="q"], form[action="/search"] input[name="q"]'
      );

      searchInputs.forEach(input => this.setupInput(input));

      // Global click outside to dismiss active dropdown
      document.addEventListener('click', (e) => {
        if (!e.target.closest('#header-search-form') && !e.target.closest('#blog-search-form') && !e.target.closest('.search-bar') && !e.target.closest('.search-page-form') && !e.target.closest('.predictive-search-dropdown')) {
          this.closeAllDropdowns();
        }
      });

      // Escape key to dismiss
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' || e.key === 'Esc') {
          this.closeAllDropdowns();
        }
      });
    }

    setupInput(input) {
      if (input.dataset.predictiveSearchBound) return;
      input.dataset.predictiveSearchBound = 'true';
      input.setAttribute('autocomplete', 'off');
      input.setAttribute('aria-autocomplete', 'list');
      input.setAttribute('aria-haspopup', 'true');
      input.setAttribute('aria-expanded', 'false');

      const form = input.closest('form');
      if (!form) return;

      // Ensure form has relative position for the absolute dropdown
      form.style.position = 'relative';

      // Create or locate dropdown container directly inside form
      let dropdown = form.querySelector('.predictive-search-dropdown');
      if (!dropdown) {
        dropdown = document.createElement('div');
        dropdown.className = 'predictive-search-dropdown';
        dropdown.setAttribute('role', 'listbox');
        form.appendChild(dropdown);
      }

      // Debounce timer
      let timer = null;

      input.addEventListener('input', () => {
        // Disable predictive dropdown on mobile devices (<768px) and in drawer
        if (window.innerWidth <= 768 || input.closest('.drawer-search-container')) {
          this.hideDropdown(dropdown, input);
          return;
        }

        const query = input.value.trim();
        clearTimeout(timer);

        if (query.length < this.minChars) {
          this.hideDropdown(dropdown, input);
          return;
        }

        this.showLoading(dropdown, input, query);

        timer = setTimeout(() => {
          this.fetchResults(query, dropdown, input);
        }, this.debounceTime);
      });

      input.addEventListener('focus', () => {
        if (window.innerWidth <= 768 || input.closest('.drawer-search-container')) {
          return;
        }
        const query = input.value.trim();
        if (query.length >= this.minChars) {
          if (dropdown.innerHTML.trim() !== '' && !dropdown.querySelector('.predictive-search-status')) {
            this.showDropdown(dropdown, input);
          } else {
            this.showLoading(dropdown, input, query);
            this.fetchResults(query, dropdown, input);
          }
        }
      });

      // Keyboard navigation (Arrow keys + Enter)
      input.addEventListener('keydown', (e) => {
        const items = dropdown.querySelectorAll('.predictive-search-link');
        if (!items.length || !dropdown.classList.contains('is-active')) return;

        if (e.key === 'ArrowDown') {
          e.preventDefault();
          this.focusedIndex = (this.focusedIndex + 1) % items.length;
          this.updateFocus(items);
        } else if (e.key === 'ArrowUp') {
          e.preventDefault();
          this.focusedIndex = (this.focusedIndex - 1 + items.length) % items.length;
          this.updateFocus(items);
        } else if (e.key === 'Enter' && this.focusedIndex > -1) {
          e.preventDefault();
          items[this.focusedIndex].click();
        }
      });
    }

    updateFocus(items) {
      items.forEach((item, index) => {
        if (index === this.focusedIndex) {
          item.classList.add('is-focused');
          item.scrollIntoView({ block: 'nearest' });
        } else {
          item.classList.remove('is-focused');
        }
      });
    }

    showDropdown(dropdown, input) {
      this.closeAllDropdowns(dropdown);
      dropdown.classList.add('is-active');
      input.setAttribute('aria-expanded', 'true');
      this.activeDropdown = dropdown;
      this.focusedIndex = -1;
    }

    hideDropdown(dropdown, input) {
      if (!dropdown) return;
      dropdown.classList.remove('is-active');
      if (input) input.setAttribute('aria-expanded', 'false');
      this.focusedIndex = -1;
    }

    closeAllDropdowns(except = null) {
      document.querySelectorAll('.predictive-search-dropdown').forEach(d => {
        if (d !== except) {
          d.classList.remove('is-active');
          const form = d.closest('form');
          if (form) {
            const input = form.querySelector('input[name="q"]');
            if (input) input.setAttribute('aria-expanded', 'false');
          }
        }
      });
      this.focusedIndex = -1;
    }

    showLoading(dropdown, input, query) {
      dropdown.innerHTML = `
        <div class="predictive-search-status">
          <div class="predictive-search-spinner"></div>
          <div>Buscando sugerencias para "<strong>${this.escapeHtml(query)}</strong>"...</div>
        </div>
      `;
      this.showDropdown(dropdown, input);
    }

    async fetchResults(query, dropdown, input) {
      // Check cache first
      if (this.cachedResults.has(query)) {
        this.renderResults(this.cachedResults.get(query), query, dropdown, input);
        return;
      }

      if (this.abortController) {
        this.abortController.abort();
      }
      this.abortController = new AbortController();

      const suggestEndpoint = `/search/suggest.json?q=${encodeURIComponent(query)}&resources[type]=product,page,article,collection&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor`;
      const fullSearchEndpoint = `/search?view=json&q=${encodeURIComponent(query)}`;

      try {
        const [suggestRes, fullSearchRes] = await Promise.all([
          fetch(suggestEndpoint, {
            signal: this.abortController.signal,
            headers: { 'Accept': 'application/json' }
          }).then(res => res.ok ? res.json() : null).catch(() => null),
          fetch(fullSearchEndpoint, {
            signal: this.abortController.signal,
            headers: { 'Accept': 'application/json' }
          }).then(res => res.ok ? res.json() : null).catch(() => null)
        ]);

        const suggestResults = suggestRes && suggestRes.resources ? suggestRes.resources.results : {};
        let products = suggestResults.products || [];
        let pages = suggestResults.pages || [];
        let articles = (suggestResults.articles || []).filter(a => !a.url.includes('/blogs/instagram/'));
        let collections = suggestResults.collections || [];

        // Si la búsqueda extendida devolvió resultados (hasta 50 elementos), rescatamos los que suggest.json truncó
        if (fullSearchRes && Array.isArray(fullSearchRes.items)) {
          const validFullArticles = fullSearchRes.items.filter(item => {
            return (item.object_type === 'article' || (item.url && item.url.includes('/blogs/'))) && !item.url.includes('/blogs/instagram/');
          });
          const validFullPages = fullSearchRes.items.filter(item => {
            return item.object_type === 'page' || (item.url && item.url.includes('/pages/'));
          });
          const validFullProducts = fullSearchRes.items.filter(item => {
            return item.object_type === 'product' || item.price !== null || (item.url && item.url.includes('/products/'));
          });

          // Combinar artículos reales sin duplicados
          validFullArticles.forEach(fullArt => {
            if (!articles.some(a => a.url === fullArt.url || a.title === fullArt.title)) {
              articles.push(fullArt);
            }
          });

          // Combinar páginas si suggest no las trajo
          if (pages.length === 0 && validFullPages.length > 0) {
            pages = validFullPages;
          }

          // Combinar productos si suggest no los trajo
          if (products.length === 0 && validFullProducts.length > 0) {
            products = validFullProducts;
          }
        }

        const mergedResults = {
          products: products,
          pages: pages,
          articles: articles,
          collections: collections
        };

        this.cachedResults.set(query, mergedResults);
        this.renderResults(mergedResults, query, dropdown, input);
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.warn('Predictive search error:', err);
        }
      }
    }

    renderResults(results, query, dropdown, input) {
      if (!results) {
        this.renderEmpty(query, dropdown);
        return;
      }

      const products = results.products || [];
      const pages = results.pages || [];
      const articles = (results.articles || []).filter(a => !a.url.includes('/blogs/instagram/'));
      const collections = results.collections || [];

      const totalResults = products.length + pages.length + articles.length + collections.length;

      if (totalResults === 0) {
        this.renderEmpty(query, dropdown);
        return;
      }

      let html = '<div class="predictive-search-content">';

      // 1. Artículos y Guías del Blog
      if (articles.length > 0) {
        html += `
          <div class="predictive-search-section">
            <div class="predictive-search-heading">
              <i class="fa fa-newspaper-o" aria-hidden="true"></i> Artículos y Guías (${articles.length})
            </div>
            <ul class="predictive-search-list">
              ${articles.map(article => {
                const img = article.image || (article.featured_image ? article.featured_image.url : null);
                return `
                  <li class="predictive-search-item" role="option">
                    <a href="${article.url}" class="predictive-search-link">
                      ${img 
                        ? `<img src="${img}" class="predictive-search-thumb" alt="${this.escapeHtml(article.title)}">`
                        : `<div class="predictive-search-icon-badge"><i class="fa fa-file-text-o"></i></div>`
                      }
                      <div class="predictive-search-details">
                        <div class="predictive-search-title">${this.highlightMatch(article.title, query)}</div>
                      </div>
                    </a>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        `;
      }

      // 2. Páginas Informativas y Soluciones
      if (pages.length > 0) {
        html += `
          <div class="predictive-search-section">
            <div class="predictive-search-heading">
              <i class="fa fa-bookmark-o" aria-hidden="true"></i> Soluciones y Páginas (${pages.length})
            </div>
            <ul class="predictive-search-list">
              ${pages.map(page => `
                <li class="predictive-search-item" role="option">
                  <a href="${page.url}" class="predictive-search-link">
                    <div class="predictive-search-icon-badge"><i class="fa fa-globe"></i></div>
                    <div class="predictive-search-details">
                      <div class="predictive-search-title">${this.highlightMatch(page.title, query)}</div>
                      <div class="predictive-search-meta">
                        <span>Página informativa</span>
                      </div>
                    </div>
                  </a>
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      // 3. Productos (si existen)
      if (products.length > 0) {
        html += `
          <div class="predictive-search-section">
            <div class="predictive-search-heading">
              <i class="fa fa-cube" aria-hidden="true"></i> Productos (${products.length})
            </div>
            <ul class="predictive-search-list">
              ${products.map(product => {
                const img = product.image || (product.featured_image ? product.featured_image.url : null);
                return `
                  <li class="predictive-search-item" role="option">
                    <a href="${product.url}" class="predictive-search-link">
                      ${img 
                        ? `<img src="${img}" class="predictive-search-thumb" alt="${this.escapeHtml(product.title)}">`
                        : `<div class="predictive-search-icon-badge"><i class="fa fa-tag"></i></div>`
                      }
                      <div class="predictive-search-details">
                        <div class="predictive-search-title">${this.highlightMatch(product.title, query)}</div>
                        <div class="predictive-search-meta">
                          ${product.price ? `<span class="predictive-search-price">${this.formatMoney(product.price)}</span>` : ''}
                        </div>
                      </div>
                    </a>
                  </li>
                `;
              }).join('')}
            </ul>
          </div>
        `;
      }

      // 4. Colecciones
      if (collections.length > 0) {
        html += `
          <div class="predictive-search-section">
            <div class="predictive-search-heading">
              <i class="fa fa-folder-open-o" aria-hidden="true"></i> Categorías (${collections.length})
            </div>
            <ul class="predictive-search-list">
              ${collections.map(col => `
                <li class="predictive-search-item" role="option">
                  <a href="${col.url}" class="predictive-search-link">
                    <div class="predictive-search-icon-badge"><i class="fa fa-th-large"></i></div>
                    <div class="predictive-search-details">
                      <div class="predictive-search-title">${this.highlightMatch(col.title, query)}</div>
                    </div>
                  </a>
                </li>
              `).join('')}
            </ul>
          </div>
        `;
      }

      html += '</div>';

      // Footer con enlace a todos los resultados
      html += `
        <div class="predictive-search-footer">
          <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search-view-all">
            Ver todos los resultados para "<strong>${this.escapeHtml(query)}</strong>" <i class="fa fa-arrow-right" style="font-size: 11px;"></i>
          </a>
        </div>
      `;

      dropdown.innerHTML = html;
      this.showDropdown(dropdown, input);
    }

    renderEmpty(query, dropdown) {
      dropdown.innerHTML = `
        <div class="predictive-search-status">
          <i class="fa fa-search" style="font-size: 24px; color: #94a3b8; margin-bottom: 8px; display: block;"></i>
          <div>No se encontraron resultados para "<strong>${this.escapeHtml(query)}</strong>"</div>
        </div>
        <div class="predictive-search-footer">
          <a href="/search?q=${encodeURIComponent(query)}" class="predictive-search-view-all">
            Ir a la página de búsqueda <i class="fa fa-arrow-right" style="font-size: 11px;"></i>
          </a>
        </div>
      `;
    }

    highlightMatch(text, query) {
      if (!text) return '';
      const safeText = this.escapeHtml(text);
      if (!query) return safeText;

      const escapedQuery = query.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      const regex = new RegExp(`(${escapedQuery})`, 'gi');
      return safeText.replace(regex, '<span class="predictive-search-highlight">$1</span>');
    }

    escapeHtml(str) {
      if (!str) return '';
      return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&#039;');
    }

    formatMoney(cents) {
      if (typeof cents === 'string' && (cents.includes('$') || cents.includes('€'))) return cents;
      const num = Number(cents) / 100;
      if (isNaN(num)) return '';
      return '$' + num.toLocaleString('es-MX', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    }
  }

  // Auto-instantiate
  new PredictiveSearchHandler();

  // Mobile Drawer & Submenu Accordion Handler (Autonomous Overlay Modal)
  function initMobileNavigation() {
    const hamburgerBtn = document.getElementById('ModernHamburgerBtn');
    const closeBtn = document.getElementById('ModernDrawerCloseBtn');
    const backdrop = document.getElementById('NavDrawerBackdrop');
    const drawer = document.getElementById('NavDrawer');
    const toggleBtns = document.querySelectorAll('.mobile-nav__toggle');

    function openDrawer(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (drawer) drawer.classList.add('is-open');
      if (backdrop) backdrop.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      document.body.style.overflow = 'hidden';
    }

    function closeDrawer(e) {
      if (e) {
        e.preventDefault();
        e.stopPropagation();
      }
      if (drawer) drawer.classList.remove('is-open');
      if (backdrop) backdrop.classList.remove('is-open');
      document.documentElement.style.overflow = '';
      document.body.style.overflow = '';
    }

    if (hamburgerBtn) {
      hamburgerBtn.addEventListener('click', openDrawer);
    }

    // Also support any other .js-drawer-open-left
    document.querySelectorAll('.js-drawer-open-left').forEach(btn => {
      btn.addEventListener('click', openDrawer);
    });

    if (closeBtn) {
      closeBtn.addEventListener('click', closeDrawer);
    }

    if (backdrop) {
      backdrop.addEventListener('click', closeDrawer);
    }

    // Escape key
    document.addEventListener('keydown', function(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        if (drawer && drawer.classList.contains('is-open')) {
          closeDrawer(e);
        }
      }
    });

    // Unified Navigation & Accordion Delegated Handler
    if (drawer) {
      drawer.addEventListener('click', function(e) {
        // Toggle chevron clicked
        const toggleBtn = e.target.closest('.mobile-nav__toggle');
        if (toggleBtn) {
          e.preventDefault();
          e.stopPropagation();
          const item = toggleBtn.closest('.mobile-nav__item');
          const header = toggleBtn.closest('.mobile-nav__has-sublist');
          if (item) item.classList.toggle('is-expanded');
          if (header) header.classList.toggle('is-expanded');
          return;
        }

        // Link clicked
        const link = e.target.closest('a');
        if (link) {
          const href = link.getAttribute('href');
          if (href === '#' || href === 'javascript:void(0)' || !href) {
            e.preventDefault();
            const item = link.closest('.mobile-nav__item');
            const header = link.closest('.mobile-nav__has-sublist');
            if (item) item.classList.toggle('is-expanded');
            if (header) header.classList.toggle('is-expanded');
          } else {
            closeDrawer();
          }
        }
      });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initMobileNavigation);
  } else {
    initMobileNavigation();
  }

  /* ==========================================================================
     SMART CHAT WIDGET SUPPRESSION & COOKIE PERSISTENCE
     Prevents auto-popping of chat widgets (Zoho SalesIQ, Zendesk, etc.)
     once closed by the user, requiring manual click to re-open.
     ========================================================================== */
  (function initChatPersistence() {
    var COOKIE_NAME = 'arroba_chat_dismissed';
    var EXPIRY_DAYS = 30;

    function isChatDismissed() {
      try {
        var match = document.cookie.match(new RegExp('(?:^|;\\s*)' + COOKIE_NAME + '=([^;]*)'));
        if (match && match[1] === 'true') return true;
        if (window.localStorage && window.localStorage.getItem(COOKIE_NAME) === 'true') return true;
      } catch(e) {}
      return false;
    }

    function setChatDismissed(dismissed) {
      try {
        var val = dismissed ? 'true' : 'false';
        var d = new Date();
        d.setTime(d.getTime() + (EXPIRY_DAYS * 24 * 60 * 60 * 1000));
        document.cookie = COOKIE_NAME + '=' + val + '; expires=' + d.toUTCString() + '; path=/; SameSite=Lax';
        if (window.localStorage) {
          if (dismissed) {
            window.localStorage.setItem(COOKIE_NAME, 'true');
          } else {
            window.localStorage.removeItem(COOKIE_NAME);
          }
        }
      } catch(e) {}
    }

    function suppressAutoOpen() {
      if (!isChatDismissed()) return;

      // 1. Zoho SalesIQ API integration
      if (window.$zoho && window.$zoho.salesiq) {
        try {
          if (typeof window.$zoho.salesiq.chatwindow !== 'undefined' && typeof window.$zoho.salesiq.chatwindow.visible === 'function') {
            window.$zoho.salesiq.chatwindow.visible('hide');
          }
          if (typeof window.$zoho.salesiq.floatwindow !== 'undefined' && typeof window.$zoho.salesiq.floatwindow.visible === 'function') {
            window.$zoho.salesiq.floatwindow.visible('hide');
          }
          if (typeof window.$zoho.salesiq.proactive !== 'undefined' && typeof window.$zoho.salesiq.proactive.status === 'function') {
            window.$zoho.salesiq.proactive.status('off');
          }
        } catch(e) {}
      }

      // 2. Zendesk Web Widget API integration
      if (typeof window.zE === 'function') {
        try {
          window.zE('webWidget', 'close');
        } catch(e) {}
      }

      // 3. Shopify Inbox API integration
      if (window.ShopifyChat) {
        try {
          if (typeof window.ShopifyChat.hideChat === 'function') {
            window.ShopifyChat.hideChat();
          }
        } catch(e) {}
      }

      // 4. DOM-level suppression for proactively opened popup windows / balloons
      try {
        var proactivePopups = document.querySelectorAll(
          '.zsiq_invitation, .siq_proact, .zsiq_popout, .zsiq_theme1.zsiq_mwt, [id*="zsiq"] [class*="window"]:not([class*="min"]), [class*="chat-widget-window"]'
        );
        proactivePopups.forEach(function(el) {
          if (!el.dataset.userOpened) {
            el.style.display = 'none';
          }
        });
      } catch(e) {}
    }

    // Hook into Zoho SalesIQ ready callback
    window.$zoho = window.$zoho || { salesiq: { values: {}, ready: function() {} } };
    var existingReady = window.$zoho.salesiq.ready;
    window.$zoho.salesiq.ready = function(embedinfo) {
      if (typeof existingReady === 'function') {
        try { existingReady(embedinfo); } catch(e) {}
      }

      try {
        if (window.$zoho.salesiq.chatwindow) {
          window.$zoho.salesiq.chatwindow.onclose = function() {
            setChatDismissed(true);
          };
        }
        if (window.$zoho.salesiq.floatwindow) {
          window.$zoho.salesiq.floatwindow.onclose = function() {
            setChatDismissed(true);
          };
        }
      } catch(e) {}

      if (isChatDismissed()) {
        suppressAutoOpen();
        setTimeout(suppressAutoOpen, 1000);
        setTimeout(suppressAutoOpen, 2500);
      }
    };

    // Global Delegated click listener to catch ANY close or minimize button
    document.addEventListener('click', function(e) {
      var closeTarget = e.target.closest(
        '.zsiq_close, .zsiq_icon-close, [class*="zsiq"][class*="close"], [aria-label*="close" i], [aria-label*="cerrar" i], [class*="chat"] [class*="close"], #dummy-chat-button-iframe'
      );
      if (closeTarget) {
        setChatDismissed(true);
        return;
      }

      // If user clicks the small launcher/button to open chat manually
      var openTarget = e.target.closest(
        '#zsiq_float, .zsiq_float_main, .zsiq_flt_rel, [class*="chat-launcher"], [aria-label*="chat" i]'
      );
      if (openTarget) {
        try {
          var proactivePopups = document.querySelectorAll(
            '.zsiq_invitation, .siq_proact, .zsiq_popout, .zsiq_theme1.zsiq_mwt'
          );
          proactivePopups.forEach(function(el) {
            el.dataset.userOpened = 'true';
            el.style.display = '';
          });
        } catch(e) {}
      }
    }, true);

    // Initial check & periodic checks on load
    if (isChatDismissed()) {
      suppressAutoOpen();
      if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', function() {
          suppressAutoOpen();
          setTimeout(suppressAutoOpen, 600);
          setTimeout(suppressAutoOpen, 1800);
          setTimeout(suppressAutoOpen, 3500);
        });
      } else {
        setTimeout(suppressAutoOpen, 600);
        setTimeout(suppressAutoOpen, 1800);
        setTimeout(suppressAutoOpen, 3500);
      }

      if (typeof MutationObserver !== 'undefined') {
        var observer = new MutationObserver(function() {
          if (isChatDismissed()) {
            suppressAutoOpen();
          }
        });
        observer.observe(document.body || document.documentElement, {
          childList: true,
          subtree: true
        });
        setTimeout(function() {
          observer.disconnect();
        }, 10000);
      }
    }
  })();

  /* ==========================================================================
     YOUTUBE & EMBEDDED IFRAME BORDER CLEANER
     ========================================================================== */
  function cleanVideoBorders() {
    var iframes = document.querySelectorAll('iframe[src*="youtube"], iframe[src*="youtu.be"], iframe[src*="vimeo"], .video-wrapper iframe');
    iframes.forEach(function(iframe) {
      iframe.setAttribute('frameborder', '0');
      iframe.style.border = 'none';
      iframe.style.outline = 'none';
      iframe.style.boxShadow = 'none';
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', cleanVideoBorders);
  } else {
    cleanVideoBorders();
  }
})();

